// First-party analytics. Events go to our own backend, which stores them and
// forwards to PostHog when POSTHOG_API_KEY is configured server-side.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ID_KEY = "bbc_distinct_id";
const SESSION_KEY = "bbc_session_id";
const USER_KEY = "bbc_analytics_user";
const ATTRIBUTION_KEY = "bbc_attribution";
const CAMPAIGN_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
  "ttclid",
  "li_fat_id",
];

export const LANDING_VARIANTS = ["quiz", "book"];
const REVIEW_ATTRIBUTION_DAYS = 30;

const uuid = () =>
  window.crypto?.randomUUID ? window.crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const readJson = (store, key) => {
  try {
    return JSON.parse(store.getItem(key));
  } catch {
    return null;
  }
};

const distinctId = () => {
  try {
    let id = window.localStorage.getItem(ID_KEY);
    if (!id) {
      id = uuid();
      window.localStorage.setItem(ID_KEY, id);
    }
    return id;
  } catch {
    return "anonymous";
  }
};

const sessionId = () => {
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = uuid();
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

export const attribution = () => {
  const data = readJson(window.localStorage, ATTRIBUTION_KEY) || {};
  if (data.review_clicked_at) {
    const age = Date.now() - new Date(data.review_clicked_at).getTime();
    if (!(age >= 0) || age > REVIEW_ATTRIBUTION_DAYS * 86400000) {
      delete data.review_id;
      delete data.review_clicked_at;
    }
  }
  return data;
};

// A testimonial click is credited with any session booked in the following 30 days.
export const tagReviewClick = (reviewId) => {
  if (!reviewId) return;
  const previous = readJson(window.localStorage, ATTRIBUTION_KEY) || {};
  try {
    window.localStorage.setItem(
      ATTRIBUTION_KEY,
      JSON.stringify({ ...previous, review_id: reviewId, review_clicked_at: new Date().toISOString() })
    );
  } catch {
    /* storage blocked — attribution is best-effort */
  }
};

export const captureAttribution = () => {
  const search = new URLSearchParams(window.location.search);
  const tags = {};
  CAMPAIGN_PARAMS.forEach((key) => {
    const value = search.get(key);
    if (value) tags[key] = value;
  });
  const variant = search.get("v") || search.get("variant");
  if (variant && LANDING_VARIANTS.includes(variant)) tags.variant = variant;
  const previous = readJson(window.localStorage, ATTRIBUTION_KEY);
  if (Object.keys(tags).length || !previous) {
    const data = {
      ...tags,
      referrer: document.referrer || previous?.referrer || "direct",
      landing_page: previous?.landing_page || window.location.pathname,
      first_seen_at: previous?.first_seen_at || new Date().toISOString(),
    };
    try {
      window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(data));
    } catch {
      // private browsing — attribution simply is not persisted
    }
  }
};

export const landingVariant = () => {
  const search = new URLSearchParams(window.location.search);
  const fromUrl = search.get("v") || search.get("variant");
  if (fromUrl && LANDING_VARIANTS.includes(fromUrl)) return fromUrl;
  return attribution().variant || "default";
};

export const track = (event, properties = {}) => {
  const identity = readJson(window.localStorage, USER_KEY) || {};
  const body = JSON.stringify({
    event,
    distinct_id: distinctId(),
    session_id: sessionId(),
    user_id: identity.user_id || null,
    email: identity.email || null,
    path: window.location.pathname,
    properties,
    attribution: attribution(),
  });
  try {
    fetch(`${API}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // analytics must never break a user flow
  }
};

export const identifyUser = (user) => {
  if (!user) return;
  try {
    window.localStorage.setItem(
      USER_KEY,
      JSON.stringify({ user_id: user.user_id || null, email: user.email || null })
    );
  } catch {
    // ignore
  }
};

export const resetAnalytics = () => {
  try {
    window.localStorage.removeItem(USER_KEY);
  } catch {
    // ignore
  }
};
